import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Archive, Boxes, CheckCircle2, Code2, Download,
  Globe2, Laptop, Loader2, MonitorSmartphone, MousePointer2, PackageOpen,
  PlugZap, ShieldCheck, Sparkles, Wrench,
} from 'lucide-react'
import { getSession, getToken } from '@/lib/auth'
import './DownloadCenterPanel.css'

type ProductKey = 'browser' | 'native' | 'wordpress'
type DownloadKey = ProductKey | 'browser_beta'
type VersionState = Record<ProductKey, string | null>

const PRODUCT_ENDPOINTS: Record<ProductKey, { version: string; download: string; filename: (version: string | null) => string }> = {
  browser: {
    version: '/api/dashboard/extension/version',
    download: '/api/dashboard/extension/download',
    filename: (version) => `OPS-Universal-Browser-${version || 'latest'}.zip`,
  },
  native: {
    version: '/api/dashboard/native-input/version',
    download: '/api/dashboard/native-input/download',
    filename: () => 'Install OPS Native Input Helper.exe',
  },
  wordpress: {
    version: '/api/dashboard/wordpress-plugin/version',
    download: '/api/dashboard/wordpress-plugin/download',
    filename: (version) => `OPS-WordPress-Connector-${version || 'latest'}.zip`,
  },
}

function authHeaders(): Record<string, string> {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function ProductVisual({ kind }: { kind: ProductKey }) {
  if (kind === 'browser') {
    return (
      <div className="ops-dl-visual ops-dl-visual-browser" aria-hidden="true">
        <div className="ops-dl-browser-orb"><Globe2 /></div>
        <div className="ops-dl-mini-ops"><HexMark /></div>
      </div>
    )
  }
  if (kind === 'native') {
    return (
      <div className="ops-dl-visual ops-dl-visual-native" aria-hidden="true">
        <div className="ops-dl-laptop"><Laptop /></div>
        <div className="ops-dl-pointer"><MousePointer2 /></div>
        <div className="ops-dl-mini-ops"><HexMark /></div>
      </div>
    )
  }
  return (
    <div className="ops-dl-visual ops-dl-visual-wordpress" aria-hidden="true">
      <div className="ops-dl-wordpress-orb"><Globe2 /></div>
      <div className="ops-dl-wp-letter">W</div>
      <div className="ops-dl-mini-ops"><HexMark /></div>
    </div>
  )
}

function HexMark() {
  return <div className="ops-dl-hex"><div /></div>
}

function Feature({ children }: { children: string }) {
  return <li><CheckCircle2 className="h-4 w-4" /><span>{children}</span></li>
}

export function DownloadCenterPanel() {
  const [versions, setVersions] = useState<VersionState>({ browser: null, native: null, wordpress: null })
  const [downloading, setDownloading] = useState<DownloadKey | null>(null)
  const [betaVersion, setBetaVersion] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [category, setCategory] = useState('all')
  const [extensionConnection, setExtensionConnection] =
    useState<'idle' | 'connecting' | 'connected'>('idle')
  const connectionPopup = useRef<Window | null>(null)
  const browserHandoff = useRef<{ state: string; intent: 'connect' | 'manage' } | null>(null)
  const adminOrigin = 'https://admin.opsbakersguild.com'

  // Only the authenticated OPS Gateway window that this Dashboard opened may
  // report connection status. Messages are status-only, never login tokens.
  useEffect(() => {
    const handleConnectionStatus = (event: MessageEvent) => {
      if (event.origin !== adminOrigin ||
          !connectionPopup.current || event.source !== connectionPopup.current ||
          !event.data) return

      if (event.data.type === 'ops-browser-handoff-ready') {
        const pending = browserHandoff.current
        if (!pending || event.data.state !== pending.state) return
        browserHandoff.current = null
        const popup = connectionPopup.current
        const normalConnect = adminOrigin + '/browser/connect?' +
          (pending.intent === 'manage' ? 'manage=1' : 'connect_intent=1')
        if (event.data.existing_session === true) {
          popup.location.replace(normalConnect)
          return
        }
        // Verify the existing login with its issuing authority, never in a URL.
        // A failed network/bridge request is NOT proof the user logged out.
        let confirmedExpired = false
        void (async () => {
          // Never use the optional static/service token from getToken().
          // A signed-in Dashboard can hold either a verified Core JWT or a
          // Gateway-issued opaque MFA session ID. Both are checked server-side.
          const token = getSession()?.token || ''
          if (!token) {
            throw new Error('No valid signed-in Dashboard session')
          }
          const response = await fetch('/api/auth/browser-connect/handoff/issue', {
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ state: pending.state, intent: pending.intent }),
            cache: 'no-store',
          })
          const payload = await response.json().catch(() => ({}))
          if (!response.ok) {
            // Only an explicit authentication rejection means re-login.
            // A 403, timeout, CORS error, or 503 cannot invalidate the
            // locally active Dashboard session.
            confirmedExpired = response.status === 401
            throw new Error(payload?.error || 'Session handoff unavailable')
          }
          if (payload?.success !== true ||
              typeof payload.consume_url !== 'string') {
            throw new Error('Session handoff unavailable')
          }
          const destination = new URL(payload.consume_url)
          if (destination.origin !== adminOrigin ||
              destination.pathname !== '/auth/browser-connect/handoff/consume' ||
              !/^[a-f0-9]{64}$/i.test(destination.searchParams.get('ticket') || '')) {
            throw new Error('Untrusted session handoff destination')
          }
          if (connectionPopup.current === popup && !popup.closed) {
            popup.location.replace(destination.toString())
          }
        })().catch(() => {
          // Never force a second login merely because the bridge is offline,
          // a popup timed out, or a server reported a temporary failure.
          // Only a verified 401 from the Gateway means the current session
          // cannot be reused. The normal login/MFA route remains available.
          if (connectionPopup.current === popup && !popup.closed) {
            if (confirmedExpired) {
              popup.location.replace(normalConnect)
            } else {
              popup.close()
              connectionPopup.current = null
            }
          }
          setExtensionConnection('idle')
          setError(confirmedExpired
            ? 'Your existing Dashboard session has expired. Please sign in again.'
            : 'OPS could not complete the secure connection handoff. Your Dashboard session remains open; please retry Connect Extension.')
        })
        return
      }

      if (event.data.type !== 'ops-universal-connection-status') return
      const status = event.data.status
      if (status === 'connected') {
        setExtensionConnection('connected')
        connectionPopup.current.close()
        connectionPopup.current = null
      } else if (status === 'cancelled' || status === 'failed' || status === 'disconnected') {
        setExtensionConnection('idle')
        if (status === 'disconnected') {
          connectionPopup.current.close()
          connectionPopup.current = null
        }
      }
    }
    const resetOnSignout = () => {
      // saveToken also emits ops-auth-change on a valid token refresh.
      // Only an actual logout/expired Dashboard session cancels this handoff.
      if (getSession()) return
      browserHandoff.current = null
      const popup = connectionPopup.current
      if (popup && !popup.closed) popup.close()
      connectionPopup.current = null
      setExtensionConnection('idle')
    }
    window.addEventListener('message', handleConnectionStatus)
    window.addEventListener('ops-auth-change', resetOnSignout)
    return () => {
      window.removeEventListener('message', handleConnectionStatus)
      window.removeEventListener('ops-auth-change', resetOnSignout)
    }
  }, [])

  const openExtensionConnection = () => {
    if (extensionConnection === 'connecting' && connectionPopup.current && !connectionPopup.current.closed) {
      connectionPopup.current.focus()
      return
    }
    const isManage = extensionConnection === 'connected'
    const state = crypto.randomUUID()
    const intent = isManage ? 'manage' : 'connect'
    // Open synchronously from the click so Chrome does not block the popup.
    // Gateway sets an HttpOnly one-time state cookie, then posts a ready
    // acknowledgement before we request its single-use session ticket.
    const popup = window.open('about:blank', 'ops-extension-connection',
      'popup=yes,width=590,height=730,resizable=yes,scrollbars=yes')
    if (!popup) {
      setError('Chrome blocked the secure OPS connection window. Allow pop-ups for the OPS Dashboard and try again.')
      setExtensionConnection('idle')
      return
    }
    connectionPopup.current = popup
    browserHandoff.current = { state, intent }
    const prepare = new URL(adminOrigin + '/auth/browser-connect/handoff/prepare')
    prepare.searchParams.set('state', state)
    prepare.searchParams.set('dashboard_origin', window.location.origin)
    popup.location.replace(prepare.toString())
    popup.focus()
    setError('')
    if (!isManage) {
      setExtensionConnection('connecting')
      window.setTimeout(() => {
        setExtensionConnection(current => current === 'connecting' ? 'idle' : current)
      }, 95000)
    }
  }

  const fetchVersions = useCallback(async () => {
    const entries = await Promise.all((Object.keys(PRODUCT_ENDPOINTS) as ProductKey[]).map(async (key) => {
      try {
        const res = await fetch(PRODUCT_ENDPOINTS[key].version, { headers: authHeaders(), cache: 'no-store' })
        if (!res.ok) return [key, null] as const
        const data = await res.json()
        return [key, typeof data.version === 'string' ? data.version : null] as const
      } catch {
        return [key, null] as const
      }
    }))
    setVersions(Object.fromEntries(entries) as VersionState)
    // Beta release has its own manifest. Missing/unpublished beta never affects Stable.
    try {
      const response = await fetch('/api/dashboard/extension/beta/version', {
        headers: authHeaders(), cache: 'no-store',
      })
      const record = response.ok ? await response.json() : null
      setBetaVersion(record?.channel === 'beta' && typeof record.version === 'string' ? record.version : null)
    } catch {
      setBetaVersion(null)
    }
  }, [])

  useEffect(() => {
    const focus = sessionStorage.getItem('ops_download_focus')
    if (focus === 'wordpress') setCategory('plugins')
    else if (focus === 'browser') setCategory('browser')
    else if (focus === 'native') setCategory('desktop')
    if (focus) sessionStorage.removeItem('ops_download_focus')
    void fetchVersions()
  }, [fetchVersions])

  const download = async (key: DownloadKey) => {
    setDownloading(key)
    setError('')
    try {
      const beta = key === 'browser_beta'
      const endpointUrl = beta ? '/api/dashboard/extension/beta/download' : PRODUCT_ENDPOINTS[key].download
      const res = await fetch(endpointUrl, { headers: authHeaders(), cache: 'no-store' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || 'Download failed.')
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = beta
        ? `OPS-Universal-Browser-${betaVersion || 'beta'}-Beta.zip`
        : PRODUCT_ENDPOINTS[key].filename(versions[key])
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed.')
    } finally {
      setDownloading(null)
    }
  }

  const filters = useMemo(() => [
    ['all', 'All Downloads', Boxes],
    ['browser', 'Browser Extensions', Globe2],
    ['desktop', 'Desktop Software', Laptop],
    ['plugins', 'Plugins', PlugZap],
    ['microsoft', 'Microsoft', MonitorSmartphone],
    ['developer', 'Developer Tools', Code2],
  ] as const, [])

  const show = (group: string) => category === 'all' || category === group

  return (
    <div className="ops-dl-page">
      <section className="ops-dl-hero">
        <div className="ops-dl-hero-copy">
          <span className="ops-dl-eyebrow">GET CONNECTED</span>
          <h1>Download <span>Center</span></h1>
          <p>Everything you need to connect your tools, browsers and devices to OPS. Secure, integrated and kept in one canonical place.</p>
        </div>
        <div className="ops-dl-hero-art" aria-hidden="true">
          <div className="ops-dl-orbit ops-dl-orbit-a" />
          <div className="ops-dl-orbit ops-dl-orbit-b" />
          <div className="ops-dl-hero-mark"><HexMark /></div>
          <div className="ops-dl-hero-word">OPS</div>
          <div className="ops-dl-hero-sub">Built for what&apos;s next.</div>
        </div>
      </section>

      <nav className="ops-dl-filters" aria-label="Download categories">
        {filters.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setCategory(id)} className={category === id ? 'is-active' : ''}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>

      {error && <div className="ops-dl-error">{error}</div>}

      <section className="ops-dl-grid">
        {show('browser') && (
          <article className="ops-dl-card ops-dl-card-featured">
            <div className="ops-dl-tag is-green">Stable</div>
            <ProductVisual kind="browser" />
            <h2>OPS Universal Browser</h2>
            <div className="ops-dl-kind">Chromium browser extension</div>
            <p>Secure OPS browser collaboration with user-owned device identity and local website scope controls.</p>
            <ul>
              <Feature>Secure OPS device registration</Feature>
              <Feature>Local website scope controls</Feature>
              <Feature>Physical collaboration compatible</Feature>
              <Feature>Stable OPS extension identity</Feature>
            </ul>
            <div className="ops-dl-chips">
              <span>Chrome</span><span>Edge</span><span>Chromium-based</span>
            </div>
            <div className="ops-dl-meta">Stable version {versions.browser || 'checking…'} · Chromium</div>
            <button className="ops-dl-download" onClick={() => download('browser')} disabled={downloading !== null}>
              {downloading === 'browser' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
              {downloading === 'browser' ? 'Preparing stable…' : 'Download Stable Version'}
            </button>
            <div className="ops-dl-beta-section">
              <div className="ops-dl-beta-label">{betaVersion ? `Beta version ${betaVersion}` : 'No beta release currently available'}</div>
              <button className="ops-dl-download ops-dl-beta-download" type="button"
                onClick={() => download('browser_beta')}
                disabled={downloading !== null || !betaVersion}
                aria-describedby="ops-dl-beta-note">
                {downloading === 'browser_beta' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
                {downloading === 'browser_beta' ? 'Preparing beta…' : 'Download Beta Version'}
              </button>
              <span className="ops-dl-beta-note" id="ops-dl-beta-note">Testing release. Stable stays unchanged; install beta only when you intend to test an upgrade.</span>
            </div>
            <button className="ops-dl-connect" type="button"
              onClick={openExtensionConnection}
              aria-describedby="ops-dl-connect-note"
              title="Request confirmation from your installed OPS Chrome extension">
              <PlugZap className="h-5 w-5" />
              {extensionConnection === 'connected' ? 'Connected — Manage'
                : extensionConnection === 'connecting' ? 'Confirm in Extension…'
                : 'Connect Extension'}
            </button>
            <span className="ops-dl-detail-note" id="ops-dl-connect-note">
              {extensionConnection === 'connected'
                ? 'OPS is connected to this browser. Select Manage to review or disconnect.'
                : 'The installed extension asks you to Allow or Cancel. No additional TOTP; local mouse and keyboard switches remain under your control.'}
            </span>
          </article>
        )}

        {show('desktop') && (
          <article className="ops-dl-card">
            <div className="ops-dl-tag is-green">Required for physical control</div>
            <ProductVisual kind="native" />
            <h2>OPS Native Input Helper</h2>
            <div className="ops-dl-kind">Windows application</div>
            <p>Local companion required only when OPS needs to move the real Windows pointer or type through the operating system.</p>
            <ul>
              <Feature>Normal-user Windows installation</Feature>
              <Feature>OPS-signed six-hour leases</Feature>
              <Feature>Human takeover protection</Feature>
              <Feature>No OPS credentials stored</Feature>
            </ul>
            <div className="ops-dl-chips"><span>Windows 10 / 11</span><span>x64</span></div>
            <div className="ops-dl-meta">Version {versions.native || '1.0.1'} · One-click Windows installer</div>
            <button className="ops-dl-download" onClick={() => download('native')} disabled={downloading === 'native'}>
              {downloading === 'native' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
              {downloading === 'native' ? 'Preparing…' : 'Download for Windows'}
            </button>
            <span className="ops-dl-detail-note">Installer verifies the reviewed binary SHA-256 before installation.</span>
          </article>
        )}

        {show('plugins') && (
          <article className="ops-dl-card">
            <div className="ops-dl-tag is-purple">Popular</div>
            <ProductVisual kind="wordpress" />
            <h2>OPS WordPress Connector</h2>
            <div className="ops-dl-kind">WordPress plugin</div>
            <p>Connect WordPress securely to OPS for site health, content operations, diagnostics and governed collaboration.</p>
            <ul>
              <Feature>Secure site connection</Feature>
              <Feature>Current source built on demand</Feature>
              <Feature>Role-aware OPS governance</Feature>
              <Feature>Simple WordPress upload install</Feature>
            </ul>
            <div className="ops-dl-chips"><span>WordPress</span></div>
            <div className="ops-dl-meta">Version {versions.wordpress || 'current'} · Built fresh</div>
            <button className="ops-dl-download" onClick={() => download('wordpress')} disabled={downloading === 'wordpress'}>
              {downloading === 'wordpress' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
              {downloading === 'wordpress' ? 'Building…' : 'Download Plugin'}
            </button>
            <span className="ops-dl-detail-note">Uses the existing Core GitHub-backed ZIP builder.</span>
          </article>
        )}

        {show('microsoft') && (
          <article className="ops-dl-card ops-dl-card-small">
            <div className="ops-dl-tag">Coming soon</div>
            <div className="ops-dl-small-icon"><MonitorSmartphone /></div>
            <h3>OPS Microsoft Connector</h3>
            <p>Microsoft 365 integration packages and tenant setup tools will live here when released.</p>
            <button className="ops-dl-secondary" disabled><Sparkles className="h-4 w-4" /> Coming soon</button>
          </article>
        )}

        {show('developer') && (
          <article className="ops-dl-card ops-dl-card-small">
            <div className="ops-dl-tag">Developer</div>
            <div className="ops-dl-small-icon"><Wrench /></div>
            <h3>OPS Developer Tools</h3>
            <p>SDKs, integration helpers and developer documentation can be published here as OPS expands.</p>
            <button className="ops-dl-secondary" disabled><Code2 className="h-4 w-4" /> Planned</button>
          </article>
        )}

        {category === 'all' && (
          <article className="ops-dl-card ops-dl-card-small">
            <div className="ops-dl-tag">Archive</div>
            <div className="ops-dl-small-icon"><Archive /></div>
            <h3>Previous Versions</h3>
            <p>Immutable versioned releases are retained so controlled rollback packages can be surfaced safely later.</p>
            <button className="ops-dl-secondary" disabled><PackageOpen className="h-4 w-4" /> Archive coming soon</button>
          </article>
        )}
      </section>

      <section className="ops-dl-trust">
        <div><ShieldCheck /><span><b>Secure downloads</b><small>Canonical OPS distribution</small></span></div>
        <div><PackageOpen /><span><b>Versioned releases</b><small>Immutable release paths</small></span></div>
        <div><Sparkles /><span><b>One download home</b><small>Plugins, browser and desktop</small></span></div>
        <div><Wrench /><span><b>Built to expand</b><small>New OPS software can plug in here</small></span></div>
      </section>
    </div>
  )
}
